/**
 * M4S-5777 X14 — the statistics settings belong to the crosstab, not to the browser.
 *
 * Until X14 the significance levels, the test, what is compared, Mean and Median, decimals, the
 * low-base thresholds and "completes only" were browser preferences (`BROWSER_ONLY_SETTING_KEYS`,
 * M4S-5613), so two people opening one crosstab could see different letters, and an export carried
 * whichever browser made it. They are now one block of record state,
 * `ai_layout.state.statSettings`, and this module is the one home of what that block holds and how
 * it is read. Spec: docs/pdlc/crosstabs-stat-settings.md.
 *
 *  - `STAT_SETTING_KEYS` is the literal list of the thirteen keys. Each is also filed in
 *    `RECORD_SETTING_KEYS` (compute-inputs.ts), and a test checks the two agree; neither list is
 *    derived from the other.
 *  - `parseStatSettings` is the one read, on the route's write and on every read: `ai_layout` is
 *    JSONB and the dirty check compares JSON, so the block always comes back in one canonical
 *    shape (fixed key order, percents, a second level only below the first).
 *  - `startingStatSettings` is what a new crosstab starts on: the defaults, and on an iOOH study
 *    `IOOH_CONFIDENCE` (95% with 90% lowercase). Never a literal pair here.
 *  - `resolveStatSettings` is what an opened crosstab shows: the record's block, else (a crosstab
 *    saved before X14) what this browser remembered for it, else the starting values. A legacy
 *    record is stamped by its first edit, never by opening it (decision ST3).
 *  - `layoutConfidencePatch` and `daniSettingsPatch` are what a DANI, TPBP, template or brand-lift
 *    flow may change: the significance only while nobody has chosen the crosstab's statistics,
 *    never over a saved crosstab's (decision ST2, the BT-2084 precedent for `weighted`). "Chosen"
 *    is not the binding: a create binds on its first autosave (CREATE_DELAY_MS is 0), before
 *    anyone can ask DANI or upload a banner plan, so the workspace keeps its own flag
 *    (`statsChosenRef`): an opened record with a block or remembered values, a copy, or a change
 *    made in the rail.
 *
 * Pure: no React, no storage, no network.
 */

import { SIG_CORRECTIONS, type SigCorrection } from './sig-correction';
import { standardFields, type StatsStandard } from './stats-standard';
import {
    BROWSER_ONLY_SETTING_KEYS,
    DEFAULT_SETTINGS,
    type CrosstabSettings,
} from '@/app/(private)/ai/components/Crosstabs/compute-inputs';
import {
    IOOH_CONFIDENCE,
    normalizeLayoutConfidence,
    toConfidence2Pct,
    toConfidencePct,
} from '@/app/(private)/ai/components/Crosstabs/sig-confidence';

/**
 * The statistics settings a crosstab saves, in the canonical order the block is written in.
 * Literal on purpose: a new statistics setting is added here and to `RECORD_SETTING_KEYS` by hand.
 */
export const STAT_SETTING_KEYS = [
    'confidence',
    'confidence2',
    'sigScope',
    'sigCorrection',
    'statTest',
    'sigStyle',
    'showMean',
    'showMedian',
    'decimalPlaces',
    'lowBaseWarn',
    'suppressionThreshold',
    'suppressionEnabled',
    'filterToAllSurveys',
    // M4S-5777 (X72-X81), appended so every older block keeps its key order.
    'showStdDev',
    'showStdErr',
    'showSigma',
    'showTop1Box',
    'referenceCols',
    // M4S-5777: which statistics rules the crosstab was saved under (`StatRules`).
    'statRules',
] as const;

export type StatSettingKey = (typeof STAT_SETTING_KEYS)[number];

/** The statistics fields of the workspace settings, without the block's version. */
export interface StatFields {
    confidence: number;
    confidence2: number | null;
    sigScope: CrosstabSettings['sigScope'];
    sigCorrection: SigCorrection;
    statTest: CrosstabSettings['statTest'];
    sigStyle: SigStyle;
    showMean: boolean;
    showMedian: boolean;
    decimalPlaces: CrosstabSettings['decimalPlaces'];
    lowBaseWarn: number;
    suppressionThreshold: number;
    suppressionEnabled: boolean;
    filterToAllSurveys: boolean;
    showStdDev: boolean;
    showStdErr: boolean;
    showSigma: boolean;
    showTop1Box: boolean;
    referenceCols: string[];
    statRules: StatRules;
}

/**
 * M4S-5777 (X76, X77, X80): the statistics rules a crosstab's numbers follow. 2 is today's: a
 * matrix's statistics weighted and drawn on every scale matrix (with Median, standard deviation and
 * standard error), NPS on matrices, and low bases greyed by each question's own base. 1 is the
 * rules a crosstab saved before them was made under; its numbers and greying stay as they were
 * until someone changes one of its statistics, which moves it to 2 (the conditions' `legacyRules`
 * pattern: flagged on open, upgraded only by an edit).
 */
export type StatRules = 1 | 2;
export const CURRENT_STAT_RULES: StatRules = 2;

/**
 * The settings today's rules govern. A person changing one of them on a crosstab saved under the
 * old rules has asked for these numbers again, so the change moves it to today's rules
 * (`withStatRulesUpgrade`). Any other edit (a rename, a row, decimals) leaves its rules as they are.
 */
export const STAT_RULES_KEYS: readonly string[] = [
    'showMean',
    'showMedian',
    'showStdDev',
    'showStdErr',
    'showNPS',
    'suppressionThreshold',
    'suppressionEnabled',
];

/** The settings after `key` changed: moved to today's rules when the change is one they govern. */
export function withStatRulesUpgrade<T extends { statRules?: unknown }>(
    settings: T,
    key: string,
): T {
    return settings.statRules === 1 && STAT_RULES_KEYS.includes(key)
        ? { ...settings, statRules: CURRENT_STAT_RULES }
        : settings;
}

/** `ai_layout.state.statSettings`: the fields above, versioned. */
export interface StatSettings extends StatFields {
    v: 1;
}

/** The significance markers the rail offers. */
export type SigStyle = 'letters' | 'arrows' | 'none';

const SIG_SCOPES: readonly CrosstabSettings['sigScope'][] = ['group', 'all', 'total', 'reference'];
const STAT_TESTS: readonly CrosstabSettings['statTest'][] = ['auto', 'z', 'chi2'];
const SIG_STYLES: readonly SigStyle[] = ['letters', 'arrows', 'none'];
const DECIMALS: readonly CrosstabSettings['decimalPlaces'][] = [0, 1, 2];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

const oneOf = <T>(value: unknown, allowed: readonly T[], fallback: T): T =>
    allowed.includes(value as T) ? (value as T) : fallback;

const bool = (value: unknown, fallback: boolean): boolean =>
    typeof value === 'boolean' ? value : fallback;

/** A whole, non-negative count (a base threshold), else the fallback. */
const count = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0
        ? Math.round(value)
        : fallback;

/** A confidence percent below 100, else the fallback. A fraction (0.95) reads as its percent. */
const confidencePct = (value: unknown, fallback: number): number => {
    const pct = toConfidencePct(value, fallback);
    return pct < 100 ? pct : fallback;
};

/** The defaults each field falls back to: `DEFAULT_SETTINGS`, never a second copy of them. */
function defaultFields(): StatFields {
    const d = DEFAULT_SETTINGS;
    return {
        confidence: d.confidence,
        confidence2: d.confidence2,
        sigScope: d.sigScope,
        sigCorrection: d.sigCorrection,
        statTest: d.statTest,
        sigStyle: d.sigStyle as SigStyle,
        showMean: d.showMean,
        showMedian: d.showMedian,
        decimalPlaces: d.decimalPlaces,
        lowBaseWarn: d.lowBaseWarn,
        suppressionThreshold: d.suppressionThreshold,
        suppressionEnabled: d.suppressionEnabled,
        filterToAllSurveys: d.filterToAllSurveys,
        showStdDev: d.showStdDev,
        showStdErr: d.showStdErr,
        showSigma: d.showSigma,
        showTop1Box: d.showTop1Box,
        referenceCols: [...d.referenceCols],
        statRules: d.statRules === 1 ? 1 : CURRENT_STAT_RULES,
    };
}

/** The fallback for a block that was saved before `statRules`: the rules it was made under. */
const legacyFallback = (fields: StatFields): StatFields => ({ ...fields, statRules: 1 });

/** The most reference columns a crosstab keeps, and the longest id: column ids are short. */
const MAX_REFERENCE_COLS = 50;
const MAX_COL_ID = 500;

/**
 * M4S-5777 (X72): the reference columns in their one canonical form: distinct non-empty strings
 * other than Total, sorted by code units (JSONB returns arrays in order, but two saves that picked
 * the same columns in another order must sign alike), at most fifty. Anything else is null.
 */
export function parseReferenceCols(raw: unknown): string[] | null {
    if (!Array.isArray(raw)) return null;
    const ids = new Set<string>();
    for (const v of raw) {
        if (typeof v === 'string' && v && v.length <= MAX_COL_ID && v !== 'total') ids.add(v);
    }
    return [...ids].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)).slice(0, MAX_REFERENCE_COLS);
}

/**
 * The fields of an untrusted object, each checked, in canonical order. A field that is missing or
 * of the wrong shape takes `fallback`'s. A second level is kept only when it is below the first,
 * as the rail allows (it offers only lower levels, and drops the second when the first goes below
 * it).
 */
function fieldsOf(raw: Record<string, unknown>, fallback: StatFields): StatFields {
    const confidence = confidencePct(raw.confidence, fallback.confidence);
    const second = 'confidence2' in raw ? toConfidence2Pct(raw.confidence2) : fallback.confidence2;
    return {
        confidence,
        confidence2: second !== null && second < confidence ? second : null,
        sigScope: oneOf(raw.sigScope, SIG_SCOPES, fallback.sigScope),
        sigCorrection: oneOf(raw.sigCorrection, SIG_CORRECTIONS, fallback.sigCorrection),
        statTest: oneOf(raw.statTest, STAT_TESTS, fallback.statTest),
        sigStyle: oneOf(raw.sigStyle, SIG_STYLES, fallback.sigStyle),
        showMean: bool(raw.showMean, fallback.showMean),
        showMedian: bool(raw.showMedian, fallback.showMedian),
        decimalPlaces: oneOf(raw.decimalPlaces, DECIMALS, fallback.decimalPlaces),
        lowBaseWarn: count(raw.lowBaseWarn, fallback.lowBaseWarn),
        suppressionThreshold: count(raw.suppressionThreshold, fallback.suppressionThreshold),
        suppressionEnabled: bool(raw.suppressionEnabled, fallback.suppressionEnabled),
        filterToAllSurveys: bool(raw.filterToAllSurveys, fallback.filterToAllSurveys),
        showStdDev: bool(raw.showStdDev, fallback.showStdDev),
        showStdErr: bool(raw.showStdErr, fallback.showStdErr),
        showSigma: bool(raw.showSigma, fallback.showSigma),
        showTop1Box: bool(raw.showTop1Box, fallback.showTop1Box),
        referenceCols: parseReferenceCols(raw.referenceCols) ?? [...fallback.referenceCols],
        statRules: raw.statRules === 1 || raw.statRules === 2 ? raw.statRules : fallback.statRules,
    };
}

const block = (fields: StatFields): StatSettings => ({ v: 1, ...fields });

/**
 * The stored block in its one canonical form, or null when there is none: not an object, or a
 * version this code does not know (a record from a later build is read as having no block rather
 * than guessed at). Each field that does not parse falls back to `DEFAULT_SETTINGS`'s, so a body
 * or a column with one bad field keeps the rest.
 */
export function parseStatSettings(raw: unknown): StatSettings | null {
    if (!isPlainObject(raw) || raw.v !== 1) return null;
    // A stored block without `statRules` was saved before it: its rules are the old ones.
    return block(fieldsOf(raw, legacyFallback(defaultFields())));
}

/** The block for the settings a session has now, as the save sends it. Never null. */
export function statSettingsOf(settings: Partial<CrosstabSettings>): StatSettings {
    return block(fieldsOf(settings as Record<string, unknown>, defaultFields()));
}

/** The workspace settings a block sets: its fields, without the version. */
export function statFields(stats: StatSettings): StatFields {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { v, ...fields } = stats;
    return fields;
}

/**
 * What a new crosstab's statistics start on: the defaults, and on an iOOH study its significance,
 * `IOOH_CONFIDENCE` (95% with 90% lowercase, docs/pdlc/crosstabs-starting-setup.md §4).
 */
export function startingStatSettings({
    iooh,
    standard,
    display,
}: {
    iooh: boolean;
    /**
     * M4S-5777 X67: the study type's statistics standard, from its setup (X8). Given, it decides
     * the significance fields and `iooh` is not read; absent, the rule before X67 (a crosstab that
     * predates standards, or a caller with no setup).
     */
    standard?: StatsStandard;
    /** M4S-5777 X8: the setup's starting display of the numbers. */
    display?: Partial<Pick<StatFields, 'decimalPlaces' | 'sigStyle' | 'showMean' | 'showMedian'>>;
}): StatSettings {
    const defaults = defaultFields();
    if (standard) {
        return block(fieldsOf({ ...defaults, ...display, ...standardFields(standard) }, defaults));
    }
    return block(iooh ? { ...defaults, ...IOOH_CONFIDENCE } : defaults);
}

/**
 * The statistics an opened crosstab shows, from the first source that has them:
 *
 *  1. the record's own block (`record`, already parsed);
 *  2. a crosstab saved before X14 has none, so what this browser remembered for it (`legacy`, read
 *     by `readLegacyStatFields` in workspace-cache.ts, the only reader of browser slots). Opening it
 *     writes nothing; its first edit saves the block (ST3);
 *  3. otherwise the starting values.
 *
 * A legacy value that does not parse takes the starting value for that field, never a guess.
 */
export function resolveStatSettings({
    record,
    legacy,
    iooh,
}: {
    record: StatSettings | null;
    legacy: Partial<Record<StatSettingKey, unknown>> | null;
    iooh: boolean;
}): StatSettings {
    if (record) return record;
    const starting = startingStatSettings({ iooh });
    if (!legacy) return starting;
    // A crosstab saved before X14 was made under the old statistics rules too.
    return block(fieldsOf(legacy, legacyFallback(statFields(starting))));
}

/** Whether a settings key is one of the thirteen. */
export function isStatSettingKey(key: string): key is StatSettingKey {
    return (STAT_SETTING_KEYS as readonly string[]).includes(key);
}

/**
 * The significance a layout flow (TPBP, template, brand-lift, DANI) applies, ST2: while nobody has
 * chosen the crosstab's statistics (a new crosstab, saved by its create or not) it is the flow's,
 * coerced to percents as it always was (`normalizeLayoutConfidence`); once they are chosen (an
 * opened record that holds them, a copy, or a change in the rail) it is nothing, so the crosstab
 * keeps its levels and the flow does not quietly save new ones. The flows hard-code 95, or 95
 * with 90, which would reset a saved 90% or 99%.
 */
export function layoutConfidencePatch(
    layout: { confidence?: number | null; confidence2?: number | null },
    prev: { confidence: number; confidence2: number | null },
    { statsChosen }: { statsChosen: boolean },
): Partial<Pick<StatFields, 'confidence' | 'confidence2'>> {
    if (statsChosen) return {};
    return normalizeLayoutConfidence(layout, prev);
}

const DISPLAY_KEYS: ReadonlySet<string> = new Set<string>(BROWSER_ONLY_SETTING_KEYS);
const SIGNIFICANCE_KEYS: ReadonlySet<string> = new Set<string>(['confidence', 'confidence2']);

/**
 * What a DANI `crosstab_layout` block's `settings` may change, ST2. It can carry any key, so it is
 * filtered rather than spread:
 *
 *  - a display key (`BROWSER_ONLY_SETTING_KEYS`) goes to the live settings, as before;
 *  - the statistics, while nobody has chosen them, parsed, to live AND committed settings
 *    together, so the table shows them and Run is not lit;
 *  - once they are chosen, the significance is dropped (ST2), and the other statistics DANI names
 *    (Mean, Median, decimals, the test...) go, parsed, to the live settings only: Run lights up
 *    and the person runs the change on purpose, and its save stamps it on the record;
 *  - every other key, the record's content (`weighted`, the Top 2 switches, the matrix edits) and
 *    anything unknown, is dropped. DANI does not change those through this block.
 */
export function daniSettingsPatch(
    raw: unknown,
    prev: Partial<CrosstabSettings>,
    { statsChosen }: { statsChosen: boolean },
): { live: Partial<CrosstabSettings>; committed: Partial<CrosstabSettings> } {
    if (!isPlainObject(raw)) return { live: {}, committed: {} };
    const display: Partial<CrosstabSettings> = {};
    const stats: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(raw)) {
        if (DISPLAY_KEYS.has(key)) display[key] = value;
        else if (isStatSettingKey(key)) {
            if (statsChosen && SIGNIFICANCE_KEYS.has(key)) continue;
            // The rules a crosstab follows move only by a person's edit, never by DANI.
            if (key === 'statRules') continue;
            stats[key] = value;
        }
    }
    if (Object.keys(stats).length === 0) return { live: display, committed: {} };
    const current = fieldsOf(prev as Record<string, unknown>, defaultFields());
    const next = fieldsOf(stats, current);
    if (!statsChosen) return { live: { ...display, ...next }, committed: { ...next } };
    const asked = Object.fromEntries(
        STAT_SETTING_KEYS.filter((key) => key in stats).map((key) => [key, next[key]]),
    ) as Partial<CrosstabSettings>;
    return { live: { ...display, ...asked }, committed: {} };
}
